import { useMemo } from 'react';
import { ClaimType, ClaimShopCarrier } from '../types';

export interface SelectOption {
  label: string;
  value: string;
}

interface UseClaimCarrierSelectOptionsParams {
  carrierCode?: string;
  carrierCountryCode?: string;
  carriers: ClaimShopCarrier[];
  type?: ClaimType;
}

interface Result {
  isNon7sCarrier: boolean | null;
  isPicturesOfMissingItemRequired: boolean | null;
  isDisplayCustomerPhoneNumberInputEnabled: boolean | null;
  isWeightRequired: boolean | null;
  isCarrierCaseIdAndCarrierDamageReportRequired: boolean | null;
  isDisplayCarrierCaseIdInputEnabled: boolean | null;
  carrierOptions: SelectOption[];
  carrierCountryOptions: SelectOption[];
}

export function useClaimCarrierSelectOptions({
  carrierCode,
  carrierCountryCode,
  carriers,
  type,
}: UseClaimCarrierSelectOptionsParams): Result {
  const filteredCarriers = useMemo(() => {
    if (type === 'cash_on_delivery') {
      return carriers.filter((carrier) => carrier.isNon7s);
    }

    return carriers;
  }, [carriers, type]);

  const carrierOptions = useMemo(
    () => getUniqueCarriers(filteredCarriers).map(({ name, code }) => ({ label: name || code, value: code })),
    [filteredCarriers]
  );

  const carrierCountryOptions = useMemo(() => getCarrierCountryOptions(carrierCode, filteredCarriers), [
    filteredCarriers,
    carrierCode,
  ]);

  const carrier = useMemo(
    () => filteredCarriers.find(({ code, countryCode }) => code === carrierCode && countryCode === carrierCountryCode),
    [filteredCarriers, carrierCode, carrierCountryCode]
  );

  return {
    isNon7sCarrier: carrier?.isNon7s ?? null,
    isPicturesOfMissingItemRequired: carrier?.isPicturesOfMissingItemRequired ?? null,
    isDisplayCustomerPhoneNumberInputEnabled: carrier?.isDisplayCustomerPhoneNumberInputEnabled ?? null,
    isWeightRequired: carrier?.isWeightRequired ?? null,
    isCarrierCaseIdAndCarrierDamageReportRequired:
      (type &&
        [ClaimType.PARTIAL_DAMAGE, ClaimType.WHOLE_DAMAGE, ClaimType.MISSING_ITEM].includes(type) &&
        carrier?.isCarrierCaseIdAndCarrierDamageReportRequired) ||
      null,
    isDisplayCarrierCaseIdInputEnabled:
      (type &&
        [ClaimType.PARTIAL_DAMAGE, ClaimType.WHOLE_DAMAGE, ClaimType.MISSING_ITEM].includes(type) &&
        carrier?.isDisplayCarrierCaseIdInputEnabled) ||
      null,
    carrierOptions,
    carrierCountryOptions,
  };
}

function getUniqueCarriers(carriers: ClaimShopCarrier[]): ClaimShopCarrier[] {
  return [...new Map(carriers.map((carrier) => [carrier.code, carrier])).values()];
}

function getCarrierCountryOptions(carrierCode: string | undefined, carriers: ClaimShopCarrier[]): SelectOption[] {
  return carriers
    .filter(({ code }) => code === carrierCode)
    .map((carrier) => ({ label: carrier.countryName, value: carrier.countryCode }));
}
